import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "disabled", "divider", "onClick", "isActive", "pageSize", "isLoading", "edsContext"];
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { default as withStyles } from '../../styles';
import Divider from '../../Divider/Divider';
import ListItem from '../../List/ListItem';
import ListItemText from '../../List/ListItemText';
import classNames from 'classnames';
import { Icon } from '@ellucian/ds-icons/lib';
import { withEDSContext } from '../../EDSContext/EDSContext';
import Typography from './../../Typography/Typography';
import { spacing40, sizingSmall, sizingXSmall, colorCtaBlueBase, colorCtaBlueHover, colorTextNeutral500, colorBrandNeutral250, lineHeightDefault } from '../../styles/tokens';
var styles = function styles(theme) {
  return {
    item: {
      outline: 'none',
      display: 'flex',
      justifyContent: 'center',
      cursor: 'pointer',
      color: colorCtaBlueBase,
      '& svg': {
        fill: colorCtaBlueBase
      },
      '&:active': {
        color: colorCtaBlueHover,
        backgroundColor: colorBrandNeutral250,
        '& svg': {
          fill: colorCtaBlueHover
        }
      },
      '&:hover': {
        color: colorCtaBlueHover,
        '& svg': {
          fill: colorCtaBlueHover
        }
      },
      '&:focus': {
        color: colorCtaBlueHover,
        '& svg': {
          fill: colorCtaBlueHover
        }
      }
    },
    divider: {
      '&.MuiDivider-root': {
        borderColor: theme.palette.grey['300'],
        margin: '0'
      }
    },
    mark: {
      color: colorTextNeutral500,
      backgroundColor: 'initial'
    },
    icons: {
      '& svg': {
        flexShrink: 0,
        fill: theme.palette.grey['500']
      }
    },
    paddingRightWithIcon: {
      paddingRight: spacing40
    },
    paddingLeftWithIcon: {
      paddingLeft: spacing40
    },
    itemText: {
      maxWidth: 'fit-content'
    },
    itemTextPrimary: {
      fontWeight: '700',
      lineHeight: lineHeightDefault,
      textTransform: 'uppercase'
    },
    showNextIocn: {
      marginLeft: sizingXSmall,
      fontSize: sizingSmall,
      fontWeight: '700',
      lineHeight: lineHeightDefault,
      color: colorCtaBlueBase
    }
  };
};
var ShowNextButtonItem = function ShowNextButtonItem(props) {
  var classes = props.classes,
    disabled = props.disabled,
    divider = props.divider,
    onClick = props.onClick,
    isActive = props.isActive,
    pageSize = props.pageSize,
    isLoading = props.isLoading,
    edsContext = props.edsContext,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var itemRef = /*#__PURE__*/React.createRef();

  // disabling warning for missing dpendency of itemRef
  // refer to the following link for more details on why this is the best option for this case
  // https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
  useEffect(function () {
    if (isActive) {
      itemRef.current && itemRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "srOnly",
    role: "region",
    "aria-live": "polite"
  }, isLoading ? edsContext.formatMessage('component.Search.loading') : ''), /*#__PURE__*/React.createElement(ListItem, _extends({
    className: classNames(classes.item, props.className),
    disabled: disabled,
    "aria-disabled": disabled ? true : false,
    ref: itemRef,
    onClick: disabled ? null : onClick,
    tabIndex: 0,
    button: true,
    role: "button",
    key: "showNextButton",
    "data-id": "showNextButton",
    "aria-label": edsContext.formatMessage('component.Search.showNext') + " " + pageSize
  }, rest), isLoading ? /*#__PURE__*/React.createElement(Icon, {
    name: "spinner",
    large: true,
    spin: true
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ListItemText, {
    classes: {
      root: classes.itemText,
      primary: classes.itemTextPrimary
    },
    primary: edsContext.formatMessage('component.Search.showNext') + " " + pageSize
  }), /*#__PURE__*/React.createElement(Icon, {
    name: "chevron-down",
    className: classes.showNextIocn
  }))), divider && /*#__PURE__*/React.createElement(Divider, {
    classes: {
      root: classes.divider
    },
    component: "li"
  }));
};
ShowNextButtonItem.displayName = 'ShowNextButtonItem';
ShowNextButtonItem.propTypes = {
  /**
   * The text of the item.
   */
  children: PropTypes.node,
  /**
   * If `true`, the item will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, a 1px light border is added to the bottom of the list item.
   */
  divider: PropTypes.bool,
  /**
   * @ignore
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  pageSize: PropTypes.number,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  isActive: PropTypes.bool,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * @ignore
   */
  isLoading: PropTypes.bool,
  /**
   * @ignore
   * This function fetches resource string translations. It comes from the withEDSContext HOC.
   */
  edsContext: PropTypes.object
};
export default withStyles(styles, {
  name: 'ShowNextButtonItem'
})(withEDSContext(ShowNextButtonItem));